import React, { useState, useEffect } from 'react';
import PaymentModal from './PaymentModal';
import TouchNGoPayment from './TouchNGoPayment';
import CashCalculatorModal from './CashCalculatorModal';
import PrinterModal from './PrinterModal';
import { getCurrencySymbol } from '../utils/config';
import './OrderSummary.css';

const OrderSummary = ({ cart, total, onUpdateQuantity, onRemoveItem, onClearCart, onPaymentSuccess, selectedTable }) => {
  const [currencySymbol, setCurrencySymbol] = useState('RM');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCashCalculator, setShowCashCalculator] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [showTNGPayment, setShowTNGPayment] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');

  // Tax and fees configuration
  const SST_RATE = 0.06; // 6% SST
  const SERVICE_CHARGE_RATE = 0.10; // 10% service charge

  useEffect(() => {
    const loadCurrency = async () => {
      const symbol = await getCurrencySymbol();
      setCurrencySymbol(symbol);
    };
    loadCurrency();
    
    // Debug cart data
    console.log('OrderSummary cart data:', cart);
  }, [cart]);

  // Calculate breakdown
  const calculateBreakdown = () => {
    const subtotal = cart.reduce((sum, item) => {
      const itemPrice = parseFloat(item.totalPrice) || parseFloat(item.price) || parseFloat(item.basePrice) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return sum + (itemPrice * quantity);
    }, 0);
    
    const serviceCharge = subtotal * SERVICE_CHARGE_RATE;
    const sstTax = (subtotal + serviceCharge) * SST_RATE;
    const beforeRounding = subtotal + serviceCharge + sstTax;
    const roundingAdjustment = Math.round(beforeRounding * 20) / 20 - beforeRounding; // Round to nearest 5 sen
    const finalTotal = beforeRounding + roundingAdjustment;

    return {
      subtotal: isNaN(subtotal) ? 0 : subtotal,
      serviceCharge: isNaN(serviceCharge) ? 0 : serviceCharge,
      sstTax: isNaN(sstTax) ? 0 : sstTax,
      beforeRounding: isNaN(beforeRounding) ? 0 : beforeRounding,
      roundingAdjustment: isNaN(roundingAdjustment) ? 0 : roundingAdjustment,
      finalTotal: isNaN(finalTotal) ? 0 : finalTotal
    };
  };

  const breakdown = calculateBreakdown();

  // Generate order number
  const generateOrderNumber = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const datePart = `${day}${month}${year}`;
    const timestamp = Date.now().toString().slice(-6);
    return `ORD-${datePart}AA${timestamp.slice(0, 3)}`;
  };

  const handlePayment = async (method) => {
    if (method === 'cash') {
      setShowPaymentModal(false);
      setShowCashCalculator(true);
    } else if (method === 'razorpay') {
      setShowPaymentModal(false);
      await handleRazorPayPayment();
    } else if (method === 'touchngo') {
      const orderNo = generateOrderNumber();
      setOrderNumber(orderNo);
      setShowPaymentModal(false);
      setShowTNGPayment(true);
    }
  };

  const handleRazorPayPayment = async () => {
    try {
      // Use secure PaymentService instead of direct Razorpay integration
      const { PaymentService } = await import('../utils/PaymentService');
      const paymentService = new PaymentService('https://paymentest-worker.multicoms.workers.dev');

      const orderData = {
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          qty: item.quantity,
          itemCode: item.itemCode || item.code
        })),
        totalAmount: breakdown.finalTotal,
        currency: 'MYR',
        customerInfo: {
          name: 'Customer',
          email: 'customer@example.com',
          phone: '9999999999'
        }
      };

      const result = await paymentService.initiatePayment(orderData, 'razorpay');

      if (result.success) {
        onPaymentSuccess({
          method: 'razorpay',
          ...result,
          total: breakdown.finalTotal
        });
      } else {
        alert('Payment failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    }
  };

  const handleCashPayment = (cashReceived, change) => {
    setShowCashCalculator(false);
    onPaymentSuccess({ method: 'cash', cashReceived, change });
  };

  const handleTNGSuccess = (paymentData) => {
    setShowTNGPayment(false);
    onPaymentSuccess({
      method: 'touchngo',
      orderNumber: orderNumber,
      amount: breakdown.finalTotal,
      ...paymentData
    });
  };

  const handleTNGCancel = () => {
    setShowTNGPayment(false);
    setShowPaymentModal(true);
  };

  return (
    <div className="order-summary">
      <h2>Order Summary</h2>
      {cart.length === 0 ? (
        <p className="empty-cart">No items in cart</p>
      ) : (
        <>
          <div className="cart-items">
            {cart.map((item, index) => {
              // Create unique key based on item and variations
              const variationIds = item.variations?.map(v => v.id).sort().join('-') || 'no-variations';
              const uniqueKey = `${item.id}-${variationIds}-${index}`;
              
              return (
              <div key={uniqueKey} className="cart-item">
                <div className="cart-item-details">
                  <div className="item-header">
                    <div className="item-title-section">
                      <h4 className="item-name">{item.name}</h4>
                      <span className="item-code">{item.itemCode}</span>
                    </div>
                  </div>
                  
                  {/* Show order number for multiple orders */}
                  {selectedTable && selectedTable.totalOrders > 1 && item.orderNumber && (
                    <div className="item-order-number">
                      <span className="order-number-badge">Order #{item.orderNumber}</span>
                    </div>
                  )}
                  
                  <div className="item-pricing">
                    <span className="base-price">Base: {currencySymbol}{(parseFloat(item.basePrice) || 0).toFixed(2)}</span>
                    <span className="unit-price">Unit: {currencySymbol}{(parseFloat(item.totalPrice) || parseFloat(item.price) || 0).toFixed(2)}</span>
                  </div>

                  {/* Variations Display */}
                  {item.variations && item.variations.length > 0 && (
                    <div className="item-variations">
                      {item.variations.map((variation, index) => (
                        <div key={index} className="variation-item">
                          <span className="variation-name">{variation.name}</span>
                          {variation.price > 0 && (
                            <span className="variation-price">+{currencySymbol}{variation.price.toFixed(2)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="quantity-and-total">
                    <div className="quantity-controls">
                      <button
                        onClick={() => onUpdateQuantity(item.id, item.quantity - 1, item.variations)}
                        className="qty-btn"
                      >
                        <i className="fas fa-minus"></i>
                      </button>
                      <span className="qty-display">Qty: {item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity(item.id, item.quantity + 1, item.variations)}
                        className="qty-btn"
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                    <div className="item-total">
                      <strong>{currencySymbol}{((parseFloat(item.totalPrice) || parseFloat(item.price) || 0) * parseInt(item.quantity || 0)).toFixed(2)}</strong>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => onRemoveItem(item.id, item.variations)}
                  className="remove-item-btn"
                  title="Remove Item"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
              );
            })}
          </div>

          {/* Order Breakdown */}
          <div className="order-breakdown">
            <div className="breakdown-line">
              <span>Subtotal:</span>
              <span>{currencySymbol}{breakdown.subtotal.toFixed(2)}</span>
            </div>
            <div className="breakdown-line">
              <span>Service Charge (10%):</span>
              <span>{currencySymbol}{breakdown.serviceCharge.toFixed(2)}</span>
            </div>
            <div className="breakdown-line">
              <span>SST Tax (6%):</span>
              <span>{currencySymbol}{breakdown.sstTax.toFixed(2)}</span>
            </div>
            <div className="breakdown-line">
              <span>Rounding Adjustment:</span>
              <span className={breakdown.roundingAdjustment >= 0 ? 'positive' : 'negative'}>
                {currencySymbol}{breakdown.roundingAdjustment.toFixed(2)}
              </span>
            </div>
            <div className="breakdown-line total-line">
              <strong>Total Amount:</strong>
              <strong>{currencySymbol}{breakdown.finalTotal.toFixed(2)}</strong>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="order-actions">
            <button 
              onClick={onClearCart} 
              className="action-btn cancel-btn"
              title="Clear Cart"
            >
              <i className="fas fa-times"></i>
            </button>
            
            <button 
              onClick={() => setShowPaymentModal(true)}
              className="action-btn pay-btn"
              title="Pay"
            >
              <i className="fas fa-credit-card"></i>
              <span>Pay</span>
            </button>
            
            <button 
              onClick={() => setShowPrinterModal(true)}
              className="action-btn print-btn"
              title="Print"
            >
              <i className="fas fa-print"></i>
            </button>
          </div>

          {/* Modals */}
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onPaymentSelect={handlePayment}
            total={breakdown.finalTotal}
            currencySymbol={currencySymbol}
          />

          <CashCalculatorModal
            isOpen={showCashCalculator}
            onClose={() => setShowCashCalculator(false)}
            onPaymentComplete={handleCashPayment}
            total={breakdown.finalTotal}
            currencySymbol={currencySymbol}
          />

          <PrinterModal
            isOpen={showPrinterModal}
            onClose={() => setShowPrinterModal(false)}
            cart={cart}
            breakdown={breakdown}
            currencySymbol={currencySymbol}
          />

          {/* Touch 'n Go Payment */}
          {showTNGPayment && (
            <TouchNGoPayment
              amount={breakdown.finalTotal}
              orderNumber={orderNumber}
              onSuccess={handleTNGSuccess}
              onCancel={handleTNGCancel}
            />
          )}
        </>
      )}
    </div>
  );
};

export default OrderSummary;